import { FormikProps } from 'formik'
import { FormControl, Heading, Input, Text, TextArea, VStack } from 'native-base'
import { useState } from 'react'

import { colors } from '../../utils/theme/configureTheme'
import { ControlledSelect } from '../Instructions/Form/Input/ControlledSelect'
import MerchantDeflection from './MerchantDeflection'
import { DEFLECTION_ISSUE_TYPES, ISSUE_TYPE_LABELS, IssueFormValues, IssueType } from './types'

interface DeliveryDetailsSectionProps {
  formikProps: FormikProps<IssueFormValues>
  trackingIdParam?: string
  isSubmitting: boolean
}

/** Form section for describing the delivery issue, selecting an issue type, and uploading a photo. */
export default function DeliveryDetailsSection({
  formikProps,
  trackingIdParam,
  isSubmitting,
}: DeliveryDetailsSectionProps) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formikProps
  const [isIssueDetailFocused, setIsIssueDetailFocused] = useState(false)

  return (
    <VStack space={3} mb={4}>
      <Heading
        fontSize="18px"
        fontWeight="700"
        fontFamily="Matter SemiBold"
        lineHeight="125%"
        letterSpacing="-2%"
        mb={1}
      >
        Delivery Details
      </Heading>

      <FormControl isInvalid={!!(errors.trackingId && touched.trackingId)} backgroundColor={'transparent'}>
        <FormControl.Label
          _text={{
            fontWeight: 600,
            fontSize: 13,
            color: colors.brand.softBlack,
          }}
          mt={0}
        >
          Tracking ID
        </FormControl.Label>
        <Input
          placeholder="Tracking ID"
          value={values.trackingId}
          onChangeText={handleChange('trackingId')}
          onBlur={handleBlur('trackingId')}
          autoCapitalize="characters"
          backgroundColor={trackingIdParam ? colors.brand.lightGrey : colors.brand.white}
          borderColor={colors.brand.darkGrey}
          borderWidth={2}
          fontSize={16}
          px={2}
          py={'12px'}
          disabled={!!trackingIdParam}
          readOnly={!!trackingIdParam}
          placeholderTextColor={colors.gray[100]}
          color={values.trackingId.length > 0 ? colors.brand.black : colors.gray[100]}
          _focus={{
            placeholderTextColor: colors.gray[100],
            color: values.trackingId.length > 0 ? colors.brand.black : colors.gray[100],
            ...(!!trackingIdParam && {
              backgroundColor: colors.brand.lightGrey,
              borderColor: colors.brand.darkGrey,
            }),
          }}
          _hover={{
            ...(!!trackingIdParam && {
              backgroundColor: colors.brand.lightGrey,
              borderColor: colors.brand.darkGrey,
            }),
          }}
          _disabled={{
            backgroundColor: colors.brand.lightGrey,
            borderColor: colors.brand.darkGrey,
          }}
          _readOnly={{
            backgroundColor: colors.brand.lightGrey,
            borderColor: colors.brand.darkGrey,
          }}
        />
        {errors.trackingId && touched.trackingId && (
          <FormControl.ErrorMessage backgroundColor={'transparent'}>{errors.trackingId}</FormControl.ErrorMessage>
        )}
      </FormControl>

      <FormControl isInvalid={!!(errors.issueType && touched.issueType)} backgroundColor={'transparent'}>
        <ControlledSelect name="issueType" label="Select an issue" disabled={isSubmitting}>
          <option value="selectAnIssue">Select an issue</option>
          <option value={IssueType.PACKAGE_DAMAGED}>{ISSUE_TYPE_LABELS[IssueType.PACKAGE_DAMAGED]}</option>
          <option value={IssueType.PACKAGE_MISSING}>{ISSUE_TYPE_LABELS[IssueType.PACKAGE_MISSING]}</option>
          <option value={IssueType.DRIVER_MISSED_INSTRUCTIONS}>
            {ISSUE_TYPE_LABELS[IssueType.DRIVER_MISSED_INSTRUCTIONS]}
          </option>
          <option value={IssueType.WRONG_PLACE}>{ISSUE_TYPE_LABELS[IssueType.WRONG_PLACE]}</option>
          <option value={IssueType.DELIVERY_DELAYED}>{ISSUE_TYPE_LABELS[IssueType.DELIVERY_DELAYED]}</option>
          <option value={IssueType.ORDER_CONTENTS_ISSUE}>{ISSUE_TYPE_LABELS[IssueType.ORDER_CONTENTS_ISSUE]}</option>
          <option value={IssueType.OTHER}>{ISSUE_TYPE_LABELS[IssueType.OTHER]}</option>
        </ControlledSelect>
        {errors.issueType && touched.issueType && (
          <FormControl.ErrorMessage backgroundColor={'transparent'}>{errors.issueType}</FormControl.ErrorMessage>
        )}
      </FormControl>

      {DEFLECTION_ISSUE_TYPES.has(values.issueType as IssueType) ? (
        <MerchantDeflection onGoBack={() => setFieldValue('issueType', 'selectAnIssue')} />
      ) : (
        <>
          <FormControl isInvalid={!!(errors.issueDetail && touched.issueDetail)} backgroundColor={'transparent'}>
            <FormControl.Label
              _text={{
                fontWeight: 600,
                fontSize: 13,
                color: colors.brand.softBlack,
              }}
              mt={0}
            >
              Please provide more details
            </FormControl.Label>
            {/* @ts-expect-error missing tvParallaxProperties, onTextInput */}
            <TextArea
              placeholder={isIssueDetailFocused ? '' : 'Have more details you want us to know?'}
              value={values.issueDetail}
              onChangeText={text => {
                const limited = text.slice(0, 500)
                setFieldValue('issueDetail', limited)
              }}
              onFocus={() => setIsIssueDetailFocused(true)}
              onBlur={e => {
                setIsIssueDetailFocused(false)
                handleBlur('issueDetail')(e)
              }}
              backgroundColor={colors.brand.white}
              borderColor={colors.brand.darkGrey}
              borderWidth={2}
              fontSize={16}
              px={2}
              py={'12px'}
              h={100}
              autoCompleteType="off"
              placeholderTextColor={colors.gray[100]}
              color={values.issueDetail.length > 0 ? colors.brand.black : colors.gray[100]}
              _focus={{
                placeholderTextColor: colors.gray[100],
                color: values.issueDetail.length > 0 ? colors.brand.black : colors.gray[100],
              }}
            />
            <Text fontSize={12} color={colors.brand.darkGrey} mt={1} textAlign="right">
              {values.issueDetail.length}/500
            </Text>
            {errors.issueDetail && touched.issueDetail && (
              <FormControl.ErrorMessage backgroundColor={'transparent'}>{errors.issueDetail}</FormControl.ErrorMessage>
            )}
          </FormControl>

          {/* TO DO: Re-enable photo upload as fast follow once WAF rules are configured */}
          {/* <Box>
        <Text fontSize={13} fontWeight={600} color={colors.brand.softBlack} mb={2}>
          Provide a photo (optional)
        </Text>
        <PhotoUploadActionCard onImageDataChange={handleSetImageData} />
      </Box> */}
        </>
      )}
    </VStack>
  )
}
